import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { isNil } from 'lodash'
import { EVENT_TYPES_CODES } from '../../../events/constants/EventsConstants'
import RpqsAction from '../../actions/RpqsAction'
import RpqsEventsList from './RpqsEventsList'
import useRpqsEvents from './useRpqsEvents'
import { EVENT_ORIGIN, getEventOriginLabel } from './RpqsEventsUtils'

const ACTIONS_BY_ORIGIN = {
    [EVENT_ORIGIN.RPQS]: { create: RpqsAction.createEvent, update: RpqsAction.updateEvent, delete: RpqsAction.deleteEvent },
    [EVENT_ORIGIN.REFERENTIAL]: { create: RpqsAction.createReferentialEvent, update: RpqsAction.updateReferentialEvent, delete: RpqsAction.deleteReferentialEvent },
}

// Sans egId : événements du RPQS (global + EG) et du référentiel (collectivité + ses EG).
// Avec egId : uniquement les événements du RPQS pour cette EG.
const RpqsEventsApp = ({ egId }) => {
    const { annee, idCollectivite } = useParams()
    const dispatch = useDispatch()

    const {
        managementUnits,
        collectivity,
        rpqs,
    } = useSelector(store => ({
        managementUnits: store.RpqsReducer.managementUnits,
        collectivity: store.RpqsReducer.collectivity,
        rpqs: store.RpqsReducer.rpqs,
    }), shallowEqual)

    const managementUnitId = isNil(egId) ? undefined : parseInt(egId)
    const [events, refreshEvents] = useRpqsEvents(annee, idCollectivite, managementUnitId)

    const collectivityManagementUnits = useMemo(() => managementUnits.filter(m => collectivity?.link_managementUnits?.some(l => l.managementUnitCode === m.id)), [managementUnits, collectivity])

    useEffect(() => {
        dispatch(RpqsAction.fetchAllManagementUnits())
    }, [dispatch])

    const newEvent = useMemo(() => ({
        year: parseInt(annee),
        collectivityId: parseInt(idCollectivite),
        managementUnitId,
        eventType: EVENT_TYPES_CODES.COMMENT,
    }), [annee, idCollectivite, managementUnitId])

    const onSave = ({ origin, ...event }) => {
        const actions = ACTIONS_BY_ORIGIN[origin] ?? ACTIONS_BY_ORIGIN[EVENT_ORIGIN.RPQS]
        const action = isNil(event.number) ? actions.create : actions.update
        return dispatch(action(event)).then(json => {
            if (json?.insert || json?.update) refreshEvents()
            return json
        })
    }

    const onDelete = event => {
        const actions = ACTIONS_BY_ORIGIN[event.origin] ?? ACTIONS_BY_ORIGIN[EVENT_ORIGIN.RPQS]
        return dispatch(actions.delete(event)).then(json => {
            if (json?.delete) refreshEvents()
            return json
        })
    }

    return (
        <RpqsEventsList
            events={events}
            newEvent={newEvent}
            managementUnits={isNil(managementUnitId) ? collectivityManagementUnits : managementUnits}
            canChooseManagementUnit={isNil(managementUnitId)}
            showSourceFilter={isNil(managementUnitId)}
            getOriginLabel={isNil(managementUnitId) && (event => getEventOriginLabel(event, managementUnits, collectivity, rpqs))}
            onSave={onSave}
            onDelete={onDelete}
        />
    )
}

RpqsEventsApp.propTypes = {
    egId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default RpqsEventsApp
